import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/imageUrl';
import { toast } from 'sonner';
import { Upload, X, Loader2, ImageIcon, ChevronUp, ChevronDown, Plus } from 'lucide-react';

/**
 * Visual guide for each slot — explains WHERE the photo appears on the site.
 */
const SINGLE_SLOTS = ['logo', 'hero-panorama', 'maison', 'univers-weddings', 'univers-receptions', 'univers-heritage', 'weddings-main', 'receptions-main', 'heritage-main', 'maison-fondateurs', 'realisations-weddings', 'realisations-receptions', 'realisations-heritage', 'realisations-corporate'];

const SLOT_GROUPS = [
  {
    section: 'Page d\'accueil',
    items: [
      {
        key: 'logo',
        label: 'Logo de l\'en-tête',
        desc: 'Le logo officiel affiché en haut à gauche de toutes les pages. Utilisez une version noire sur fond transparent (PNG). Sans logo, le nom « JBMH Events » s\'affiche en texte.',
        ratio: '3/1',
        recommended: 'PNG transparent, noir, ~600×200',
      },
      {
        key: 'hero-panorama',
        label: 'Image panoramique d\'accueil',
        desc: 'La grande vue aérienne (drone) affichée sous le slogan, sur toute la largeur. Aucun texte n\'est posé dessus.',
        ratio: '21/9',
        recommended: '2400×1030 ou plus, format panoramique',
      },
      {
        key: 'maison',
        label: 'La Maison — Image',
        desc: 'La photo verticale à droite du texte « Deux regards. Une même exigence. » (accueil et page La Maison).',
        ratio: '3/4',
        recommended: '1200×1600, format portrait',
      },
      { key: 'univers-weddings', label: 'Univers 01 — Weddings', desc: 'La photo verticale de la colonne Weddings dans la section Trois univers de l\'accueil.', ratio: '3/4', recommended: '1200×1600 portrait' },
      { key: 'univers-receptions', label: 'Univers 02 — Réceptions & Corporate', desc: 'La photo verticale de la colonne Réceptions privées & Corporate dans la section Trois univers de l\'accueil.', ratio: '3/4', recommended: '1200×1600 portrait' },
      { key: 'univers-heritage', label: 'Univers 03 — JBMH Héritage', desc: 'La photo verticale de la colonne JBMH Héritage dans la section Trois univers de l\'accueil.', ratio: '3/4', recommended: '1200×1600 portrait' },
    ],
  },
  {
    section: 'Page Services',
    items: [
      { key: 'service-design', label: 'Service — Event Design / Décoration', desc: 'L\'image grand format à gauche du paragraphe Event Design.', ratio: '4/5', recommended: '1200×1500 portrait' },
      { key: 'service-planning', label: 'Service — Wedding Planning', desc: 'L\'image grand format pour la section Wedding Planning.', ratio: '4/5', recommended: '1200×1500 portrait' },
      { key: 'service-service', label: 'Service — Service en salle', desc: 'L\'image grand format pour la section Service en salle & gestion.', ratio: '4/5', recommended: '1200×1500 portrait' },
      { key: 'service-stationery', label: 'Service — Papeterie', desc: 'L\'image grand format pour la section Papeterie & supports.', ratio: '4/5', recommended: '1200×1500 portrait' },
    ],
  },
  {
    section: 'Page Weddings',
    items: [
      { key: 'weddings-main', label: 'Weddings — Image principale', desc: 'La grande photo horizontale sous le titre de la page /univers/weddings. Indépendante de la photo d\'accueil « Univers 01 — Weddings ».', ratio: '2/1', recommended: '2400×1200, format paysage' },
    ],
  },
  {
    section: 'Page Réceptions & Corporate',
    items: [
      { key: 'receptions-main', label: 'Réceptions & Corporate — Image principale', desc: 'La grande photo verticale à gauche de la page /univers/receptions-corporate. Indépendante de la photo d\'accueil « Univers 02 — Réceptions & Corporate ».', ratio: '3/4', recommended: '1200×1600, format portrait' },
    ],
  },
  {
    section: 'Page JBMH Héritage',
    items: [
      { key: 'heritage-main', label: 'JBMH Héritage — Image principale', desc: 'La grande photo à droite de la page /univers/heritage, au-dessus des quatre services. Indépendante de la photo d\'accueil « Univers 03 — JBMH Héritage ».', ratio: '4/5', recommended: '1200×1500, format portrait' },
    ],
  },
  {
    section: 'Page La Maison',
    items: [
      { key: 'maison-fondateurs', label: 'La Maison — Photo des fondateurs', desc: 'La photo verticale de Mamadu et Halima dans la section « Notre histoire » de /la-maison. Tant qu\'aucune photo n\'est envoyée, une zone neutre « Photo des fondateurs à venir » s\'affiche. Indépendante de « La Maison — Image ».', ratio: '3/4', recommended: '1200×1600, format portrait' },
    ],
  },
  {
    section: 'Réalisations — couvertures des catégories (accueil)',
    items: [
      { key: 'realisations-weddings', label: 'Réalisations — Weddings', desc: 'Couverture de la catégorie Weddings dans la section « Des histoires mises en scène ».', ratio: '3/4', recommended: '1200×1600 portrait' },
      { key: 'realisations-receptions', label: 'Réalisations — Réceptions privées', desc: 'Couverture de la catégorie Réceptions privées.', ratio: '3/4', recommended: '1200×1600 portrait' },
      { key: 'realisations-heritage', label: 'Réalisations — JBMH Héritage', desc: 'Couverture de la catégorie JBMH Héritage.', ratio: '3/4', recommended: '1200×1600 portrait' },
      { key: 'realisations-corporate', label: 'Réalisations — Corporate', desc: 'Couverture de la catégorie Corporate.', ratio: '3/4', recommended: '1200×1600 portrait' },
    ],
  },
  {
    section: 'Archives — non affichées sur le site',
    items: [
      { key: 'hero', label: 'Ancien carrousel d\'accueil', desc: 'Photos de l\'ancien hero (avant la Phase 1). Elles ne sont plus affichées mais restent conservées ici.', ratio: '16/9', recommended: '—' },
      { key: 'about', label: 'Ancienne section À propos', desc: 'Photo de l\'ancienne section « D\'une passion, une maison » (avant la Phase 2). Plus affichée, conservée ici.', ratio: '3/4', recommended: '—' },
    ],
  },
];

const SitePhotosManager = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // {slotKey: true}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/site-photos');
      setSlots(data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const slotByKey = (key) => slots.find((s) => s.slot === key) || { slot: key, images: [] };

  const setSlotBusy = (key, v) => setBusy((p) => ({ ...p, [key]: v }));

  const isSingle = (key) => SINGLE_SLOTS.includes(key);
  const attach = (slotKey, url) => (isSingle(slotKey)
    ? api.put(`/admin/site-photos/${slotKey}/reorder`, { images: [url] })
    : api.post(`/admin/site-photos/${slotKey}/images`, { url }));

  const uploadAndAdd = async (slotKey, file) => {
    if (!file.type.startsWith('image/')) { toast.error('Veuillez sélectionner une image'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Image trop volumineuse (max 8 MB)'); return; }
    setSlotBusy(slotKey, true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await attach(slotKey, data.url);
      await load();
      toast.success('Photo ajoutée');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Échec de l\'upload');
    } finally {
      setSlotBusy(slotKey, false);
    }
  };

  const addUrl = async (slotKey, url) => {
    if (!url) return;
    setSlotBusy(slotKey, true);
    try {
      await attach(slotKey, url);
      await load();
      toast.success('Photo ajoutée');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setSlotBusy(slotKey, false);
    }
  };

  const removeImage = async (slotKey, idx) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await api.delete(`/admin/site-photos/${slotKey}/images/${idx}`);
      await load();
      toast.success('Photo supprimée');
    } catch { toast.error('Erreur'); }
  };

  const move = async (slotKey, idx, dir) => {
    const slot = slotByKey(slotKey);
    const imgs = [...(slot.images || [])];
    const j = idx + dir;
    if (j < 0 || j >= imgs.length) return;
    [imgs[idx], imgs[j]] = [imgs[j], imgs[idx]];
    try {
      await api.put(`/admin/site-photos/${slotKey}/reorder`, { images: imgs });
      await load();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>;

  return (
    <div data-testid="admin-sitephotos-manager">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Visuels du site</span>
        <h2 className="text-4xl font-serif text-[#111111]">Photos du site</h2>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Gérez ici toutes les photos affichées sur le site (en dehors du portfolio). Le logo, l'image panoramique,
          La Maison, les trois univers et l'image principale Weddings, l'image principale Réceptions & Corporate et l'image principale JBMH Héritage, la photo des fondateurs et les 4 couvertures Réalisations acceptent <strong>une seule photo</strong> : un nouvel envoi remplace l'actuelle.
          Les autres emplacements acceptent plusieurs photos.
          Les modifications sont visibles immédiatement après rechargement de la page concernée.
        </p>
      </div>

      {SLOT_GROUPS.map((group) => (
        <section key={group.section} className="mb-14">
          <h3 className="text-xs uppercase tracking-[0.35em] text-[#111111] mb-5 pb-3 border-b border-[#111111]/10">
            {group.section}
          </h3>
          <div className="space-y-6">
            {group.items.map((it) => (
              <SlotCard
                key={it.key}
                meta={it}
                slot={slotByKey(it.key)}
                busy={!!busy[it.key]}
                onUpload={(f) => uploadAndAdd(it.key, f)}
                onAddUrl={(u) => addUrl(it.key, u)}
                onRemove={(idx) => removeImage(it.key, idx)}
                onMove={(idx, dir) => move(it.key, idx, dir)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

const SlotCard = ({ meta, slot, busy, onUpload, onAddUrl, onRemove, onMove }) => {
  const inputRef = React.useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const images = slot.images || [];

  return (
    <div className="border border-[#111111]/10 bg-white" data-testid={`sitephoto-${slot.slot}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 border-b border-[#111111]/10">
        <div>
          <h4 className="font-serif text-xl text-[#111111]">{meta.label}</h4>
          <p className="text-xs text-[#6B6B6B] mt-1">{meta.desc}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B]/70 mt-2">
            Conseillé : {meta.recommended}
          </p>
        </div>
        <div className="shrink-0">
          <span className="inline-flex items-center gap-2 text-xs px-3 py-1.5 bg-[#F5F5F5] border border-[#111111]/30 text-[#111111]">
            <ImageIcon className="w-3.5 h-3.5 text-[#111111]" />
            {images.length} {images.length > 1 ? 'photos' : 'photo'}
          </span>
        </div>
      </div>

      <div className="p-6">
        {images.length === 0 ? (
          <p className="text-center text-[#6B6B6B] text-sm italic py-6">Aucune photo pour cet emplacement.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
            {images.map((src, idx) => (
              <div key={`${src}-${idx}`} className="relative group border border-[#111111]/10 bg-[#F5F5F5]">
                <img src={resolveImageUrl(src)} alt="" className="w-full h-32 object-contain bg-[#F5F5F5]" />
                <div className="absolute top-1 left-1 text-[10px] text-white bg-black/60 px-2 py-0.5">#{idx + 1}</div>
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => onMove(idx, -1)} disabled={idx === 0} className="w-7 h-7 bg-white/95 flex items-center justify-center disabled:opacity-30 hover:text-[#6B6B6B]" title="Monter">
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onMove(idx, 1)} disabled={idx === images.length - 1} className="w-7 h-7 bg-white/95 flex items-center justify-center disabled:opacity-30 hover:text-[#6B6B6B]" title="Descendre">
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onRemove(idx)} className="w-7 h-7 bg-white/95 flex items-center justify-center hover:text-red-500" data-testid={`sitephoto-${slot.slot}-remove-${idx}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          className={`border border-dashed ${dragOver ? 'border-[#111111] bg-[#111111]/5' : 'border-[#111111]/30 bg-[#F5F5F5]/40'} p-5 text-center transition-colors cursor-pointer`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) onUpload(e.dataTransfer.files[0]); }}
        >
          {busy ? (
            <div className="flex items-center justify-center gap-3 text-[#6B6B6B] text-xs uppercase tracking-[0.3em]">
              <Loader2 className="w-4 h-4 animate-spin" /> En cours…
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5 text-[#6B6B6B]">
              <Upload className="w-5 h-5" />
              <span className="text-xs uppercase tracking-[0.3em]">{SINGLE_SLOTS.includes(slot.slot) && images.length ? 'Glisser ou cliquer pour remplacer la photo' : 'Glisser ou cliquer pour ajouter une photo'}</span>
              <span className="text-[10px] text-[#6B6B6B]/60">JPEG / PNG / WEBP — max 8 MB</span>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ''; }}
            data-testid={`sitephoto-${slot.slot}-file-input`}
          />
        </div>

        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="…ou coller une URL d'image"
            data-testid={`sitephoto-${slot.slot}-url-input`}
            className="flex-1 border border-[#111111]/20 bg-white px-3 py-2 text-sm font-serif focus:border-[#111111] focus:outline-none transition-colors"
          />
          <button
            type="button"
            onClick={() => { onAddUrl(urlValue); setUrlValue(''); }}
            data-testid={`sitephoto-${slot.slot}-url-add`}
            className="px-4 py-2 border border-[#111111] text-xs uppercase tracking-widest hover:bg-[#333333] hover:border-[#111111] hover:text-white transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SitePhotosManager;
